import _throttle from 'lodash.throttle'

export const useScreen = (throttle: number | boolean = 200) => {
  const windowWidth = ref<number | null>(null)
  const tailwindBreakpointsPx = getTailwindBreakpointsPx()

  let resizeListenerFn: () => void

  const updateWidth = () => {
    if (typeof window === 'undefined') {
      return
    }
    if (import.meta.server) {
      return
    }

    if (typeof window === 'undefined') {
      return
    }

    if (window.innerWidth === windowWidth.value) {
      return
    }

    windowWidth.value = window.innerWidth

    if (resizeListenerFn) {
      resizeListenerFn()
    }
  }

  const listener = throttle
    ? _throttle(updateWidth, throttle as number)
    : updateWidth

  onMounted(() => {
    updateWidth()
    window.addEventListener('resize', listener)
  })

  onBeforeUnmount(() => {
    window.removeEventListener('resize', listener)
  })

  const xs = computed(() => windowWidth.value !== null && windowWidth.value <= 0)
  const sm = computed(() => windowWidth.value !== null && windowWidth.value > 0 && windowWidth.value <= tailwindBreakpointsPx.sm)
  const md = computed(() => windowWidth.value !== null && windowWidth.value > tailwindBreakpointsPx.sm && windowWidth.value <= tailwindBreakpointsPx.md)
  const lg = computed(() => windowWidth.value !== null && windowWidth.value > tailwindBreakpointsPx.md && windowWidth.value <= tailwindBreakpointsPx.lg)
  const xl = computed(() => windowWidth.value !== null && windowWidth.value > tailwindBreakpointsPx.lg && windowWidth.value <= tailwindBreakpointsPx.xl)
  const x2l = computed(() => windowWidth.value !== null && windowWidth.value > tailwindBreakpointsPx.xl && windowWidth.value <= tailwindBreakpointsPx['2xl'])
  const x3l = computed(() => windowWidth.value !== null && windowWidth.value > tailwindBreakpointsPx['2xl'] && windowWidth.value <= tailwindBreakpointsPx['3xl'])

  const isWider = (size: string | number, offset = 0) => {
    if (!windowWidth.value) {
      return null
    }
    const compareVal = typeof size === 'number' ? size : tailwindBreakpointsPx[size]
    return windowWidth.value + offset > compareVal
  }

  const isNarrower = (size: string | number, offset = 0) => {
    if (!windowWidth.value) {
      return null
    }
    const compareVal = typeof size === 'number' ? size : tailwindBreakpointsPx[size]
    return windowWidth.value + offset <= compareVal
  }

  const isBetween = (size1: string | number, size2: string | number) => {
    if (!windowWidth.value) {
      return null
    }
    let val1 = typeof size1 === 'number' ? size1 : tailwindBreakpointsPx[size1]
    let val2 = typeof size2 === 'number' ? size2 : tailwindBreakpointsPx[size2]
    if (val1 > val2) {
      [val1, val2] = [val2, val1]
    }
    return isWider(val1) && isNarrower(val2)
  }

  const getClosestBreakpoint = (breakpoints: string[]) => breakpoints
    .sort((a, b) => tailwindBreakpointsPx[a] > tailwindBreakpointsPx[b] ? -1 : 1)
    .find((breakpoint) => windowWidth.value && windowWidth.value > tailwindBreakpointsPx[breakpoint])

  const setResizeListener = (listenerFn: () => void) => {
    resizeListenerFn = listenerFn
    updateWidth()
  }

  return {
    windowWidth,
    xs,
    sm,
    md,
    lg,
    xl,
    '2xl': x2l,
    '3xl': x3l,
    isWider,
    isNarrower,
    isBetween,
    getClosestBreakpoint,
    setResizeListener,
  }
}